import { defineStore } from 'pinia'
import { useAuthStore } from './AuthStore';
import { useNodeStore } from './NodeStore';
import Api from '../api/Api'

export const useUploadLinkStore = defineStore('uploadLink', {
  state: () => ({
    uploadlinks: [],
    uploadlinkInfo: null,
  }),
  getters: {
    getUploadLinks: function(state) {
      return state.uploadlinks;
    },

    // getting the external uploadlink info
    getUploadlinkInfo: function(state) {
      return state.uploadlinkInfo;
    },
  },
  actions: {
    updateUploadlinks: function(links) {
      this.uploadlinks = links;
    },

    refreshUploadLinks: async function() {
      const nodeStore = useNodeStore();
      const authStore = useAuthStore();

      const dbUser = authStore.dbUser;
      const nodeId = nodeStore.getCurrentNodeId;
      const rootNodeId = authStore.rootNodeId;

      if (!dbUser || !dbUser.id || !nodeId) {
        return;
      }

      const response = await Api.uploadlink.getUploadLinksForNode(dbUser.id, nodeId, rootNodeId);
      this.uploadlinks = response.data;
    },

    createUploadLink: async function(payload) {
      const response = await Api.uploadlink.createUploadLink(payload);
      return response.data;
    },

    updateUploadLink: async function(payload) {
      const response = await Api.uploadlink.updateUploadLink(payload.id, payload.formData);
      return response.data;
    },

    deleteUploadLink: async function (uploadlinkID) {
      const response = await Api.uploadlink.deleteUploadLink(uploadlinkID);
      return response.data;
    },

    inviteUploadlink: async function (payload) {
      const response = await Api.uploadlink.inviteUploadlink(payload);
      return response.data;
    },

    // External call
    refreshUploadlinkInfo: async function(code) {
      const response = await Api.uploadlink.getUploadAccessInfo(code);
      this.uploadlinkInfo = response.data;
      return response.data;
    },

    // External call
    authorizeUploadlink: async function(data) {
      const response = await Api.uploadlink.authorizeUploadlink(data);
      if (response.data.accesstoken) {
        this.uploadlinkInfo = response.data;
      }
      return response.data;
    },

    checkStorage: function(units) {
      const accesstoken = this.getUploadlinkInfo.accesstoken;
      return Api.uploadlink.checkStorage(accesstoken, units);
    },

    checkTargetNodeAvailability: async function() {
      const accesstoken = this.getUploadlinkInfo.accesstoken;
      const response = await Api.uploadlink.getNodeInfo({ accesstoken });
      return response.data;
    },

    createPods: async function(payload) {
      const accessToken = this.getUploadlinkInfo.accesstoken;
      let shouldRetry = true;

      while (shouldRetry) {
        // eslint-disable-next-line no-useless-assignment
        shouldRetry = false;

        try {
          const response = await Api.uploadlink.createPods(payload, accessToken);
          return response.data;
        } catch (error) {
          if (error.response && error.response.status === 429) {
            const rateLimitReset = this._rateLimitResetTime(error.response.headers['ratelimit']);
            if (rateLimitReset) {
              console.debug(`Rate limit exceeded - retrying in ${rateLimitReset} second(s)`);
              shouldRetry = true;
              await new Promise(resolve => setTimeout(resolve, rateLimitReset * 1000));
              continue;
            }
          }

          throw error;
        }
      }
    },

    cancelUploads: async function(payload) {
      const accesstoken = this.getUploadlinkInfo.accesstoken;
      const response = await Api.uploadlink.cancelUploads(payload, accesstoken);
      return response.data;
    },

    completeMultipartUpload: async function(payload) {
      const accesstoken = this.getUploadlinkInfo.accesstoken;
      const response = await Api.uploadlink.completeMultipartUpload(payload, accesstoken);
      return response.data;
    },

    abortMultipartUpload: async function(payload) {
      const accesstoken = this.getUploadlinkInfo.accesstoken;
      const response = await Api.uploadlink.abortMultipartUpload(payload, accesstoken);
      return response.data;
    },

    /**
     * Reads filesystem item recursively (files and directories)
     * Handles drag and drop of folders with all nested content
     *
     * @param {FileSystemEntry} dropItem - The dropped file or directory entry
     * @param {string} fullPath - Current path being processed
     * @returns {Promise<File[]>} Array of files with webkitRelativePath set
     */
    readFilesystemItem(dropItem, fullPath) {
      return new Promise((resolve) => {
        // Return the file itself
        if (dropItem.isFile) {
          dropItem.file((file) => {
            let filePath = `${fullPath}/${file.name}`;
            if (filePath.startsWith('/')) {
              filePath = filePath.replace('/', '');
            }

            Object.defineProperty(file, 'webkitRelativePath', {
              get() {
                return filePath;
              },
            });

            resolve([file]);
          });
        }

        // Read the other directories recursively
        if (dropItem.isDirectory) {
          const dirReader = dropItem.createReader();
          let filesArray = [];

          // Recursive function to read all entries in batches
          const readAllEntries = async () => {
            dirReader.readEntries(async (entries) => {
              if (entries.length > 0) {
                // Process this batch of entries
                for (let i = 0; i < entries.length; i++) {
                  filesArray = filesArray.concat(await this.readFilesystemItem(entries[i], dropItem.fullPath));
                }
                // Read the next batch
                await readAllEntries();
              } else {
                // No more entries, resolve with the complete array
                resolve(filesArray);
              }
            });
          };

          // Start reading entries
          readAllEntries();
        }
      });
    },

    /**
     * Prepares hierarchical payload with roots structure for backend API
     * Groups files by their root node and organizes them into folder structure
     *
     * Structure:
     * {
     *   uploadername: string,
     *   useruuid: string,
     *   roots: [{
     *     id: nodeId,
     *     nodes: [{ relativePath: string, pods: [...] }],
     *     pods: [...]
     *   }]
     * }
     *
     * @param {Array} files - Files to upload with metadata
     * @param {string} uploadername - Name of the uploader
     * @param {string} useruuid - UUID of the user
     * @returns {Object} Hierarchical payload for createPods API
     */
    prepareHierarchicalPayload(files, uploadername, useruuid) {
      const rootNodeId = this.getUploadlinkInfo.nodeid;
      const rootsMap = new Map();

      // Initialize root node
      rootsMap.set(rootNodeId, {
        nodeid: rootNodeId,
        nodesMap: new Map(),
        pods: [],
      });

      const root = rootsMap.get(rootNodeId);

      for (const file of files) {
        const relativePath = file.meta.webkitRelativePath || '';
        const podData = {
          clientFileId: file.meta.clientFileId, // Send temporary file ID instead of UUID
          filename: file.meta.filename,
        };

        // Add lastModified if present
        if (file.meta.lastModified) {
          podData.lastModified = file.meta.lastModified.toString();
        }

        // Add note if present
        if (file.note && file.note.trim() !== '') {
          podData.note = file.note;
        }

        // File without folder path - goes to root pods array
        if (!relativePath || relativePath.indexOf('/') === -1) {
          root.pods.push(podData);
          continue;
        }

        // Extract folder path (remove filename)
        const pathSegments = relativePath.split('/');
        pathSegments.pop(); // Remove filename
        const folderPath = pathSegments.join('/');

        // Get or create node entry for this folder
        if (!root.nodesMap.has(folderPath)) {
          root.nodesMap.set(folderPath, {
            relativePath: folderPath,
            pods: [],
          });
        }

        // Add file to its folder
        root.nodesMap.get(folderPath).pods.push(podData);
      }

      // Convert maps to array
      const roots = [];
      for (const rootData of rootsMap.values()) {
        roots.push({
          id: rootData.nodeid,
          nodes: Array.from(rootData.nodesMap.values()),
          pods: rootData.pods,
        });
      }

      // Build final payload with uploadername before roots
      return {
        uploadername,
        useruuid,
        roots,
      };
    },

    /**
     * @param {string} [rateLimitHeaderValue]
     * @return {number|null}
     * @private
     */
    _rateLimitResetTime(rateLimitHeaderValue) {
      if (!rateLimitHeaderValue) {
        return null;
      }

      const dictValues = rateLimitHeaderValue.split(', ');
      for (const aDictValue of dictValues) {
        const [key, value] = aDictValue.split('=');
        if (key === 'reset' && value !== '') {
          const remaining = parseInt(value, 10);
          if (Number.isInteger(remaining)) return remaining;
          break;
        }
      }

      return null;
    }
  },
});
